// Meal inspector — the composer for one meal slot on one date. Dish items
// (presets / grams / per-item macro override), quick-starts from the planned
// rotation dish or the last logged instance, a "describe it" path that
// characterizes a new dish, and a manual-total fallback. Explicit save;
// past dates edit exactly like today.

import { useMutation, useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { apiClient, formatApiError } from '../../lib/api';
import { addDaysYMD } from '../../lib/date';
import {
  buildMealPlan,
  isoToday,
  resolveMealLog,
  useDayData,
  validateMealPlan,
} from '../../lib/day-data';
import { C, FONT_MONO } from '../../lib/design';
import {
  characterizeDish,
  useDishes,
  useLogCharacterizedMeal,
  useLogMealItems,
  useMealItems,
  type Dish,
  type DishProposal,
  type MealItem,
  type MealItemsResponse,
} from '../../lib/dish-data';
import { dowLabel, fmt1, fmtClock, type FoodSlot } from '../../lib/ledger';
import { useRotationSelections } from '../../lib/ledger-week';
import type { MealLog, MealsEatenMap } from '../../lib/types';
import { STALE_TIME_DAILY } from '../../lib/query';
import { InspHead } from './InspHead';
import { Bar, Btn, ErrorStrip, Field, INPUT_STYLE, Lbl, Seg, Sub } from './ui';

const TITLE: Record<FoodSlot, string> = { lunch: 'Lunch', snack: 'Snack', dinner: 'Dinner' };
const MACRO_FIELDS = [['KCAL', 'cal'], ['PRO', 'p'], ['FAT', 'f'], ['CARB', 'c']] as const;
const PER_100G_FIELDS = [
  ['KCAL', 'cal_per_100g'],
  ['PRO', 'protein_per_100g'],
  ['FAT', 'fat_per_100g'],
  ['CARB', 'carb_per_100g'],
  ['ALCOHOL', 'alcohol_per_100g'],
] as const;
const ZERO: MealLog = { cal: 0, p: 0, f: 0, c: 0 };
const MAX_CHIPS = 8;
/** Free-gram entry in the preset segmented control. */
const GRAMS_SEG = '';

// Same wire shape as `useMealItems`; duplicated here because the schema is
// module-private in dish-data and the REPEAT lookup needs seven of them.
const MealItemsSchema = z.object({
  date: z.string(),
  slot: z.enum(['lunch', 'snack', 'dinner']),
  items: z.array(
    z.object({
      id: z.number(),
      date: z.string(),
      slot: z.enum(['lunch', 'snack', 'dinner']),
      position: z.number(),
      dish_id: z.number(),
      dish_name: z.string(),
      grams: z.number(),
      cal: z.number(),
      p: z.number(),
      f: z.number(),
      c: z.number(),
      created_at: z.number(),
    }),
  ),
  totals: z.object({ cal: z.number(), p: z.number(), f: z.number(), c: z.number() }),
});

type Draft = {
  dishId: number;
  grams: number;
  /** Selected preset label; null when the operator typed free grams. */
  presetLabel: string | null;
  /** Per-item macro override (the `· ADJUSTED` state); null = dish values. */
  custom: MealLog | null;
};

const STATUS_RANK: Record<Dish['rotation_status'], number> = {
  active: 0, private: 1, candidate: 2, archived: 3, rejected: 4,
};

function roundMacro(value: number, digits = 1): number {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function macrosForDish(dish: Dish, grams: number): MealLog {
  return {
    cal: roundMacro((dish.cal_per_100g * grams) / 100, 0),
    p: roundMacro((dish.protein_per_100g * grams) / 100),
    f: roundMacro((dish.fat_per_100g * grams) / 100),
    c: roundMacro((dish.carb_per_100g * grams) / 100),
  };
}

function normalizedMacros(m: MealLog): MealLog {
  return { cal: roundMacro(m.cal, 0), p: roundMacro(m.p), f: roundMacro(m.f), c: roundMacro(m.c) };
}

function sameMacros(a: MealLog, b: MealLog): boolean {
  return MACRO_FIELDS.every(([, k]) => a[k] === b[k]);
}

function sumMacros(items: MealLog[]): MealLog {
  return items.reduce<MealLog>(
    (s, m) => ({
      cal: roundMacro(s.cal + m.cal, 0),
      p: roundMacro(s.p + m.p),
      f: roundMacro(s.f + m.f),
      c: roundMacro(s.c + m.c),
    }),
    ZERO,
  );
}

/**
 * Draft row for a dish. Without `grams` the default portion is the second
 * preset (M of S/M/L) when there is one, else the first. Saved macros that
 * differ from the dish's computed values become a per-item override.
 */
function draftForDish(dish: Dish, grams?: number, savedMacros?: MealLog): Draft {
  const presets = dish.portion_presets;
  const preset = grams == null
    ? presets[Math.min(1, presets.length - 1)]
    : presets.find((p) => Math.abs(p.grams - grams) < 0.001);
  const resolvedGrams = grams ?? preset?.grams ?? 300;
  const automatic = macrosForDish(dish, resolvedGrams);
  return {
    dishId: dish.id,
    grams: resolvedGrams,
    presetLabel: preset?.label ?? null,
    custom: savedMacros && !sameMacros(savedMacros, automatic) ? savedMacros : null,
  };
}

function gramsForDraft(item: Draft, dish: Dish): number {
  const preset = item.presetLabel == null
    ? undefined
    : dish.portion_presets.find((p) => p.label === item.presetLabel);
  return preset?.grams ?? item.grams;
}

function ItemRow({
  item,
  dish,
  onChange,
  onRemove,
}: {
  item: Draft;
  dish: Dish;
  onChange: (next: Draft) => void;
  onRemove: () => void;
}) {
  const grams = gramsForDraft(item, dish);
  const m = normalizedMacros(item.custom ?? macrosForDish(dish, grams));
  const segOptions: Array<[string, string]> = [
    ...dish.portion_presets.map((p): [string, string] => [p.label, p.label]),
    [GRAMS_SEG, 'g'],
  ];
  return (
    <div style={{ borderTop: `1px solid ${C.line}`, padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 13 }}>{dish.name}</div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${dish.name}`}
          style={{ background: 'transparent', border: 0, color: C.faint, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 12 }}
        >
          ×
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <Seg
          style={{ flex: 1 }}
          ariaLabel={`Portion for ${dish.name}`}
          options={segOptions}
          value={item.presetLabel ?? GRAMS_SEG}
          onChange={(v) => {
            const preset = dish.portion_presets.find((p) => p.label === v);
            onChange({ ...item, presetLabel: preset?.label ?? null, grams: preset?.grams ?? grams, custom: null });
          }}
        />
        <input
          className="num"
          type="number"
          min={1}
          aria-label={`Grams of ${dish.name}`}
          value={grams}
          onChange={(e) => onChange({ ...item, presetLabel: null, grams: Math.max(1, Number(e.target.value)), custom: null })}
          style={{
            width: 64,
            background: C.bg,
            color: C.text,
            border: `1px solid ${C.line}`,
            padding: '5px 8px',
            fontFamily: FONT_MONO,
            fontSize: 11,
            textAlign: 'right',
          }}
        />
        <Sub>g</Sub>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
        <Sub className="num" style={{ color: item.custom ? C.warn : C.faint }}>
          {m.cal} kcal · {fmt1(m.p)}p · {fmt1(m.f)}f · {fmt1(m.c)}c{item.custom ? ' · ADJUSTED' : ''}
        </Sub>
        <Btn ghost onClick={() => onChange({ ...item, custom: item.custom ? null : m })}>
          {item.custom ? 'USE DISH VALUES' : 'ADJUST'}
        </Btn>
      </div>
      {item.custom && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginTop: 8 }}>
          {MACRO_FIELDS.map(([label, k]) => (
            <Field
              key={k}
              label={label}
              min={0}
              step={k === 'cal' ? 1 : 0.1}
              value={item.custom![k]}
              onChange={(v) => onChange({ ...item, custom: { ...item.custom!, [k]: Math.max(0, v) } })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QuickStart({
  eyebrow,
  text,
  signal,
  onClick,
}: {
  eyebrow: string;
  text: string;
  signal?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: 'transparent',
        border: `1px dashed ${signal ? C.signal : C.line}`,
        color: signal ? C.text : C.dim,
        padding: '10px 12px',
        fontFamily: FONT_MONO,
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ color: signal ? C.signal : undefined, fontSize: 10, letterSpacing: '0.15em' }}>{eyebrow} · </span>
        {text}
      </span>
      <span style={{ color: C.signal, fontSize: 10, letterSpacing: '0.15em' }}>USE</span>
    </button>
  );
}

const CHIP = {
  background: C.panel2,
  border: `1px solid ${C.line}`,
  color: C.text,
  padding: '6px 10px',
  fontFamily: FONT_MONO,
  fontSize: 11,
  cursor: 'pointer',
} as const;

const TEXTAREA = {
  ...INPUT_STYLE,
  minHeight: 64,
  padding: 8,
  fontSize: 11,
  resize: 'vertical',
} as const;

export function MealInspector({ date, slot }: { date: string; slot: FoodSlot }) {
  const today = isoToday();
  const day = useDayData(date);
  const target = day.effectiveTarget;
  const plan = useMemo(() => buildMealPlan(target), [target]);
  const budget = plan.find((m) => m.slot === slot)!;
  const planPaused = !validateMealPlan(plan, target).valid;
  const mealsEaten: MealsEatenMap = day.todayRow?.meals_eaten ?? {};
  const savedLog = resolveMealLog(slot, mealsEaten[slot], plan);

  const itemsQuery = useMealItems(date, slot);
  const savedItems: MealItem[] = useMemo(() => itemsQuery.data?.items ?? [], [itemsQuery.data]);
  const logItems = useLogMealItems(date, slot);
  const logChar = useLogCharacterizedMeal(date, slot);
  const dishesQuery = useDishes({ includeInactive: true });
  const selectionsQuery = useRotationSelections(date, date);
  const planned = selectionsQuery.data?.get(`${date}:${slot}`) ?? null;

  // Last logged instance of this slot in the previous seven days (REPEAT).
  const priorDates = useMemo(() => [1, 2, 3, 4, 5, 6, 7].map((n) => addDaysYMD(date, -n)), [date]);
  const priorQueries = useQueries({
    queries: priorDates.map((d) => ({
      queryKey: ['meal-items', d, slot],
      queryFn: () =>
        apiClient.get<MealItemsResponse>(`/api/daily/${d}/meals/${slot}/items`, { schema: MealItemsSchema }),
      staleTime: STALE_TIME_DAILY,
    })),
  });
  const last = priorQueries.map((q) => q.data).find((d) => d && d.items.length > 0) ?? null;

  const dishes = useMemo(() => {
    const savedIds = new Set(savedItems.map((i) => i.dish_id));
    return (dishesQuery.data ?? [])
      .filter((d) => d.rotation_status === 'active' || d.rotation_status === 'private' || d.rotation_status === 'candidate' || savedIds.has(d.id))
      .sort((a, b) => STATUS_RANK[a.rotation_status] - STATUS_RANK[b.rotation_status] || a.name.localeCompare(b.name));
  }, [dishesQuery.data, savedItems]);
  const dishById = (id: number) => dishes.find((d) => d.id === id);

  const [draft, setDraft] = useState<Draft[]>([]);
  const [manual, setManual] = useState<MealLog | null>(null);
  const [q, setQ] = useState('');
  const [newItem, setNewItem] = useState(false);

  // The draft is rebased on the saved log whenever the saved log changes
  // (date/slot switch, our own save, a characterized add, CLEAR); the
  // signature guard keeps a background refetch of identical data from
  // clobbering in-progress edits.
  const savedSignature = JSON.stringify({
    log: savedLog,
    items: savedItems.map((i) => [i.dish_id, i.grams, i.cal, i.p, i.f, i.c]),
  });
  const initializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (itemsQuery.isLoading || dishesQuery.isLoading) return;
    // A freshly characterized dish lands in the meal-items cache before the
    // invalidated catalog refetches. Wait while that refetch is in flight;
    // otherwise initialise with what resolves and re-initialise once the
    // catalog catches up (the key flips from partial to full).
    const missing = savedItems.some((i) => !dishes.some((d) => d.id === i.dish_id));
    if (missing && dishesQuery.isFetching) return;
    const key = `${date}:${slot}:${missing ? 'partial' : 'full'}:${savedSignature}`;
    if (initializedFor.current === key) return;
    if (savedItems.length > 0) {
      setDraft(
        savedItems.flatMap((i) => {
          const dish = dishes.find((d) => d.id === i.dish_id);
          return dish ? [draftForDish(dish, i.grams, { cal: i.cal, p: i.p, f: i.f, c: i.c })] : [];
        }),
      );
      setManual(null);
    } else {
      setDraft([]);
      setManual(savedLog);
    }
    setQ('');
    setNewItem(false);
    initializedFor.current = key;
  });

  const payload = draft.flatMap((item) => {
    const dish = dishes.find((d) => d.id === item.dishId);
    if (!dish) return [];
    const grams = gramsForDraft(item, dish);
    return [{ dish_id: item.dishId, grams, macros: normalizedMacros(item.custom ?? macrosForDish(dish, grams)) }];
  });
  const total = manual ?? sumMacros(payload.map((p) => p.macros));
  const itemsDirty =
    JSON.stringify(payload) !==
    JSON.stringify(savedItems.map((i) => ({ dish_id: i.dish_id, grams: i.grams, macros: { cal: i.cal, p: i.p, f: i.f, c: i.c } })));
  const dirty = manual ? savedLog == null || !sameMacros(manual, savedLog) : itemsDirty;
  const canSave = dirty && (manual != null || payload.length > 0);
  const over = total.cal > budget.cal * 1.1;
  const saved = savedLog != null;
  const pending = logItems.isPending || logChar.isPending || day.mutations.daily.isPending;

  const results = useMemo(() => {
    const s = q.trim().toLocaleLowerCase();
    const pool = s
      ? dishes.filter((d) => `${d.name} ${d.description ?? ''} ${d.ingredients_summary ?? ''}`.toLocaleLowerCase().includes(s))
      : dishes.filter((d) => d.meal_types.includes(slot));
    return pool.slice(0, MAX_CHIPS);
  }, [q, dishes, slot]);

  const add = (dish: Dish, grams?: number, macros?: MealLog) => {
    setDraft((xs) => [...xs, draftForDish(dish, grams, macros)]);
    setManual(null);
    setQ('');
  };

  // Manual totals write a delta against whatever the slot currently holds so
  // the day total stays consistent (mirrors the old updateMealMacros).
  const saveManual = (next: MealLog) => {
    if (
      savedItems.length > 0 &&
      !window.confirm('Replace the logged dish items with these manual macros? The dish-item log for this slot will be detached.')
    ) return;
    const prev = savedLog ?? ZERO;
    const row = day.todayRow;
    day.mutations.daily.mutate({
      date,
      calories: Math.max(0, (row?.calories ?? 0) + next.cal - prev.cal),
      protein_g: Math.max(0, (row?.protein_g ?? 0) + next.p - prev.p),
      fat_g: Math.max(0, (row?.fat_g ?? 0) + next.f - prev.f),
      carb_g: Math.max(0, (row?.carb_g ?? 0) + next.c - prev.c),
      meals_eaten: { ...mealsEaten, [slot]: next },
    });
  };
  const clear = () => {
    if (!savedLog) return;
    const row = day.todayRow;
    day.mutations.daily.mutate({
      date,
      calories: Math.max(0, (row?.calories ?? 0) - savedLog.cal),
      protein_g: Math.max(0, (row?.protein_g ?? 0) - savedLog.p),
      fat_g: Math.max(0, (row?.fat_g ?? 0) - savedLog.f),
      carb_g: Math.max(0, (row?.carb_g ?? 0) - savedLog.c),
      meals_eaten: { ...mealsEaten, [slot]: null },
    });
  };
  const save = () => {
    if (manual) saveManual(normalizedMacros(manual));
    else logItems.mutate(payload);
  };

  const loading = itemsQuery.isLoading || dishesQuery.isLoading;
  const loadError = itemsQuery.error ?? dishesQuery.error ?? selectionsQuery.error;
  const saveError = logItems.error ?? day.mutations.daily.error;
  const loggedAt = savedItems[0]?.created_at;

  return (
    <div>
      <InspHead
        date={date}
        title={TITLE[slot]}
        right={saved && <Sub color={C.signal} className="num">LOGGED{loggedAt != null ? ` ${fmtClock(loggedAt)}` : ''}</Sub>}
      />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Lbl>BUDGET</Lbl>
          <Sub className="num">
            {budget.cal} kcal · {budget.p}p · {budget.f}f · {budget.c}c
          </Sub>
        </div>
        {planPaused && (
          <div style={{ marginTop: 10, border: `1px dashed ${C.warn}`, padding: '8px 10px' }}>
            <Lbl color={C.warn}>MEAL BUDGET PAUSED</Lbl>
            <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.5, marginTop: 4 }}>
              The daily target does not split cleanly into meals. Logging still works; the budget above is approximate.
            </div>
          </div>
        )}
        {loadError && <div style={{ marginTop: 10 }}><ErrorStrip message={formatApiError(loadError)} /></div>}
        {loading && <Sub style={{ marginTop: 12 }}>LOADING DISHES…</Sub>}

        {!saved && draft.length === 0 && !manual && (planned || last) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {planned && dishById(planned.dish_id) && (
              <QuickStart eyebrow="PLANNED" text={planned.dish_name} signal onClick={() => add(dishById(planned.dish_id)!)} />
            )}
            {last && (
              <QuickStart
                eyebrow={`REPEAT ${dowLabel(last.date)}`}
                text={last.items.map((i) => i.dish_name).join(' + ')}
                onClick={() => {
                  setDraft(
                    last.items.flatMap((i) => {
                      const dish = dishById(i.dish_id);
                      return dish ? [draftForDish(dish, i.grams, { cal: i.cal, p: i.p, f: i.f, c: i.c })] : [];
                    }),
                  );
                  setManual(null);
                }}
              />
            )}
          </div>
        )}

        {manual ? (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Lbl>MANUAL TOTAL</Lbl>
              <Btn ghost onClick={() => setManual(null)}>USE DISHES INSTEAD</Btn>
            </div>
            {savedItems.length > 0 && (
              <div style={{ marginTop: 8, border: `1px dashed ${C.warn}`, padding: '6px 10px', fontSize: 10, color: C.warn, letterSpacing: '0.05em' }}>
                SAVING MANUAL VALUES REPLACES THE DISH ITEMS FOR THIS MEAL.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 10 }}>
              {MACRO_FIELDS.map(([label, k]) => (
                <Field
                  key={k}
                  label={label}
                  min={0}
                  step={k === 'cal' ? 1 : 0.1}
                  value={manual[k]}
                  onChange={(v) => setManual({ ...manual, [k]: Math.max(0, v) })}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {draft.map((item, i) => {
              const dish = dishById(item.dishId);
              if (!dish) return null;
              return (
                <ItemRow
                  key={`${item.dishId}:${i}`}
                  item={item}
                  dish={dish}
                  onChange={(n) => setDraft((xs) => xs.map((x, j) => (j === i ? n : x)))}
                  onRemove={() => setDraft((xs) => xs.filter((_, j) => j !== i))}
                />
              );
            })}
            <div style={{ borderTop: draft.length ? `1px solid ${C.line}` : 0, paddingTop: draft.length ? 12 : 0 }}>
              <input
                type="search"
                aria-label="Search dishes"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setNewItem(false);
                }}
                placeholder={draft.length ? 'Add a side, fruit, extra…' : 'Search dishes…'}
                style={{ ...INPUT_STYLE, padding: '9px 10px', fontSize: 12 }}
              />
              {!newItem && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {results.map((d) => (
                    <button key={d.id} type="button" onClick={() => add(d)} style={CHIP}>
                      + {d.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNewItem(true)}
                    style={{ ...CHIP, background: 'transparent', border: `1px dashed ${C.line}`, color: C.dim }}
                  >
                    {results.length || !q.trim() ? 'Not listed →' : `“${q.trim()}” isn’t listed → describe it`}
                  </button>
                </div>
              )}
              {newItem && (
                <NewItem
                  slot={slot}
                  query={q.trim()}
                  appendToMeal={savedItems.length > 0 || payload.length > 0}
                  pending={logChar.isPending || logItems.isPending}
                  error={logChar.error}
                  onCancel={() => setNewItem(false)}
                  onLog={async (body) => {
                    // Commit any unsaved item edits first so the appended dish
                    // joins the meal the operator is looking at, not a stale one.
                    if (itemsDirty && payload.length > 0) await logItems.mutateAsync(payload);
                    await logChar.mutateAsync(body);
                    setNewItem(false);
                    setQ('');
                  }}
                />
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: 18, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Lbl style={{ whiteSpace: 'nowrap' }}>THIS MEAL</Lbl>
            <div className="num" style={{ fontSize: 12, color: over ? C.warn : C.text, textAlign: 'right' }}>
              {Math.round(total.cal)} <span style={{ color: C.faint }}>/ {budget.cal} kcal</span> · {fmt1(total.p)}p · {fmt1(total.f)}f · {fmt1(total.c)}c
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Bar value={total.cal} max={budget.cal} color={over ? C.warn : C.signal} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn primary disabled={!canSave || pending} onClick={save} style={{ flex: 1 }}>
            {logItems.isPending || day.mutations.daily.isPending
              ? 'SAVING…'
              : saved ? 'SAVE CORRECTION' : date === today ? 'LOG MEAL' : 'BACKFILL MEAL'}
          </Btn>
          {!manual && (
            <Btn
              onClick={() =>
                setManual(
                  savedItems.length === 0 && savedLog
                    ? savedLog
                    : payload.length
                      ? normalizedMacros(total)
                      : { cal: budget.cal, p: budget.p, f: budget.f, c: budget.c },
                )
              }
            >
              MANUAL
            </Btn>
          )}
          {saved && (
            <Btn danger disabled={pending} onClick={clear}>
              CLEAR
            </Btn>
          )}
        </div>
        {saveError && <div style={{ marginTop: 8 }}><ErrorStrip message={formatApiError(saveError)} /></div>}
      </div>
    </div>
  );
}

/** Body of `useLogCharacterizedMeal` (the reviewed proposal plus logging options). */
type CharacterizedBody = {
  dish: Omit<DishProposal, 'confidence_note' | 'retries'>;
  preset_label?: string;
  suggest_for_rotation: boolean;
  append_to_meal?: boolean;
};

/** NEW ITEM · describe (or paste a Cookidoo link) → AUTO-FILL → review → log. */
function NewItem({
  slot,
  query,
  appendToMeal,
  pending,
  error,
  onCancel,
  onLog,
}: {
  slot: FoodSlot;
  query: string;
  appendToMeal: boolean;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onLog: (body: CharacterizedBody) => Promise<void>;
}) {
  const [evidence, setEvidence] = useState('');
  const [proposal, setProposal] = useState<DishProposal | null>(null);
  const [portionLabel, setPortionLabel] = useState('');
  const [suggest, setSuggest] = useState(false);
  const characterize = useMutation({
    mutationFn: characterizeDish,
    onSuccess: (value) => {
      setProposal(value);
      setPortionLabel(value.portion_presets[0]?.label ?? '');
    },
  });
  const err = characterize.error ?? error;
  const ready = evidence.trim().length >= 10;

  const submit = () => {
    if (!proposal || !portionLabel) return;
    const { confidence_note: _confidence, retries: _retries, ...dish } = proposal;
    void onLog({
      dish: { ...dish, meal_types: proposal.meal_types?.length ? proposal.meal_types : [slot] },
      preset_label: portionLabel,
      suggest_for_rotation: suggest,
      append_to_meal: appendToMeal,
    }).then(() => {
      setProposal(null);
      setEvidence('');
      setSuggest(false);
    }, () => undefined);
  };

  return (
    <div style={{ marginTop: 8, border: `1px dashed ${C.line}`, padding: 12 }}>
      {!proposal ? (
        <>
          <Lbl>NEW ITEM · DESCRIBE OR PASTE COOKIDOO LINK</Lbl>
          <textarea
            aria-label="Describe the dish"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder={query ? `${query} — ingredients, how it was cooked…` : 'Ingredients, how it was cooked…'}
            style={{ ...TEXTAREA, marginTop: 8 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <Btn onClick={onCancel}>CANCEL</Btn>
            <Btn
              primary
              disabled={!ready || characterize.isPending}
              onClick={() =>
                characterize.mutate({
                  name: query || undefined,
                  evidence,
                  evidence_type: /cookidoo\./i.test(evidence) ? 'cookidoo' : 'description',
                })
              }
            >
              {characterize.isPending ? 'CHARACTERIZING…' : 'AUTO-FILL · REVIEW'}
            </Btn>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Lbl color={C.signal}>REVIEW · PER 100 G</Lbl>
            <Btn ghost onClick={() => setProposal(null)}>START OVER</Btn>
          </div>
          <input
            aria-label="Dish name"
            value={proposal.name}
            onChange={(e) => setProposal({ ...proposal, name: e.target.value })}
            style={{ ...INPUT_STYLE, marginTop: 8, fontSize: 12 }}
          />
          <textarea
            aria-label="Dish description"
            value={proposal.description}
            onChange={(e) => setProposal({ ...proposal, description: e.target.value })}
            style={{ ...TEXTAREA, minHeight: 48, marginTop: 6 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginTop: 8 }}>
            {PER_100G_FIELDS.map(([label, field]) => (
              <Field
                key={field}
                label={label}
                min={0}
                step={field === 'cal_per_100g' ? 1 : 0.1}
                value={proposal[field]}
                onChange={(v) => setProposal({ ...proposal, [field]: Math.max(0, v) })}
              />
            ))}
          </div>
          {proposal.portion_presets.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Lbl style={{ marginBottom: 4 }}>PORTION</Lbl>
              <Seg
                ariaLabel="Logging portion"
                options={proposal.portion_presets.map((p): [string, string] => [p.label, `${p.label} · ${p.grams}g`])}
                value={portionLabel}
                onChange={setPortionLabel}
              />
            </div>
          )}
          {proposal.cookidoo_recipe_name && (
            <Sub style={{ marginTop: 8 }}>VERIFIED COOKIDOO · {proposal.cookidoo_recipe_name}</Sub>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: C.dim, fontSize: 10, letterSpacing: '0.1em', cursor: 'pointer' }}>
            <input type="checkbox" checked={suggest} onChange={(e) => setSuggest(e.target.checked)} />
            SUGGEST FOR ROTATION
          </label>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            <Btn onClick={onCancel}>CANCEL</Btn>
            <Btn primary disabled={pending || !proposal.name.trim() || !portionLabel} onClick={submit}>
              {pending ? 'SAVING…' : appendToMeal ? 'ADD ITEM TO MEAL' : 'LOG MEAL'}
            </Btn>
          </div>
        </>
      )}
      {err && <div style={{ marginTop: 8 }}><ErrorStrip message={formatApiError(err)} /></div>}
    </div>
  );
}
